import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Save, Send, CheckCircle2, Loader2, ArrowLeft, Image as ImageIcon, Sparkles, Link2, ImagePlus } from "lucide-react";

export default function ArticleEditor() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { user } = useAuth();
  const nav = useNavigate();
  const canPublish = user?.role === "admin" || user?.role === "editor";

  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    title: "", category: "narmadapuram", kicker: "", thumbnail: "", slug: "",
    summary: "", body: "", publish_as_denva: false, is_exclusive: false,
    is_hero: false, pinned: false, pin_rank: 1,
  });
  const [status, setStatus] = useState("draft");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiImgBusy, setAiImgBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    api.get("/meta/categories").then((r) => setCats(r.data)).catch(() => {});
    if (editing) {
      api.get(`/articles/${id}`).then((r) => {
        const a = r.data;
        setForm({
          title: a.title || "", category: a.category, kicker: a.kicker || "", thumbnail: a.thumbnail || "", slug: a.slug || "",
          summary: a.summary || "", body: a.body || "", publish_as_denva: a.publish_as_denva || false,
          is_exclusive: a.is_exclusive || false,
          is_hero: a.is_hero || false, pinned: a.pinned || false, pin_rank: a.pin_rank || 1,
        });
        setStatus(a.status);
      }).catch(() => setErr("खबर लोड नहीं हो सकी"));
    }
  }, [id, editing]);

  const up = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Body editor helpers ---------------------------------------------
  const bodyRef = useRef(null);
  const inlineImgRef = useRef(null);
  const [insertingImg, setInsertingImg] = useState(false);

  const insertAtCursor = (text) => {
    const ta = bodyRef.current;
    const cur = form.body || "";
    if (!ta) { up("body", cur + "\n" + text + "\n"); return; }
    const start = ta.selectionStart ?? cur.length;
    const end = ta.selectionEnd ?? cur.length;
    const selected = cur.slice(start, end);
    const before = cur.slice(0, start);
    const after = cur.slice(end);
    const payload = typeof text === "function" ? text(selected) : text;
    const newBody = before + payload + after;
    up("body", newBody);
    setTimeout(() => {
      ta.focus();
      const pos = before.length + payload.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const insertLink = () => {
    const url = window.prompt("लिंक URL डालें (जैसे /news/slug या https://...):", "");
    if (!url) return;
    insertAtCursor((sel) => {
      const label = (sel && sel.trim()) || window.prompt("दिखाने के लिए text (title) डालें:", url) || url;
      return `[${label}](${url})`;
    });
  };

  const insertInlineImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\//.test(file.type)) { setErr("केवल image चुनें"); return; }
    if (file.size > 8 * 1024 * 1024) { setErr("Image 8MB से छोटी हो"); return; }
    setInsertingImg(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const alt = file.name.replace(/\.[^.]+$/, "");
      insertAtCursor(`\n![${alt}](${data.url})\n`);
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || "Image अपलोड विफल");
    } finally {
      setInsertingImg(false);
      if (inlineImgRef.current) inlineImgRef.current.value = "";
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr(""); setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      up("thumbnail", data.url);
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || "अपलोड विफल");
    } finally {
      setUploading(false);
    }
  };

  const genAiImage = async () => {
    if (!editing) {
      setErr("पहले खबर को draft में सहेजें, फिर stock image ला सकते हैं");
      return;
    }
    setErr(""); setAiImgBusy(true);
    try {
      const { data } = await api.post(`/stock-image/${id}`);
      up("thumbnail", data.url);
      setOk("Free stock image लग गई 🖼️");
      setTimeout(() => setOk(""), 3000);
    } catch (e2) {
      setErr(apiErr(e2.response?.data?.detail) || "Image लाने में समस्या — फिर try करें");
    } finally {
      setAiImgBusy(false);
    }
  };

  const save = async (targetStatus) => {
    setErr(""); setOk(""); setLoading(true);
    try {
      let articleId = id;
      if (editing) {
        await api.put(`/articles/${id}`, form);
      } else {
        const { data } = await api.post("/articles", { ...form, status: "draft" });
        articleId = data.id;
      }
      if (targetStatus) {
        await api.patch(`/articles/${articleId}/status`, { status: targetStatus });
      }
      setOk("सहेज लिया गया!");
      setTimeout(() => nav("/admin/articles"), 700);
    } catch (e) {
      setErr(apiErr(e.response?.data?.detail) || "सेव नहीं हो सका");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav("/admin/articles")} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-4"><ArrowLeft size={16} /> वापस</button>
      <h1 className="text-2xl font-bold text-gray-900 font-serif-dev mb-6">{editing ? "खबर संपादित करें" : "नई खबर लिखें"}</h1>

      {err && <div data-testid="editor-error" className="mb-4 text-sm text-[#D92525] bg-red-50 rounded px-4 py-2.5">{err}</div>}
      {ok && <div data-testid="editor-success" className="mb-4 text-sm text-green-700 bg-green-50 rounded px-4 py-2.5 flex items-center gap-2"><CheckCircle2 size={15} /> {ok}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-5">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">शीर्षक</span>
              <input data-testid="field-title" value={form.title} onChange={(e) => up("title", e.target.value)}
                className="mt-1 w-full border-b-2 border-gray-200 focus:border-[#D92525] outline-none py-2 text-[20px] font-serif-dev font-bold" placeholder="खबर का शीर्षक लिखें..." />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">सारांश (SEO के लिए महत्वपूर्ण)</span>
              <textarea data-testid="field-summary" value={form.summary} onChange={(e) => up("summary", e.target.value)} rows={3}
                className="mt-1 w-full border border-gray-200 rounded-lg focus:border-[#D92525] outline-none p-3 text-[15px] resize-none" placeholder="2-3 पंक्तियों में खबर का सार..." />
            </label>
            <label className="block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">खबर (मुख्य सामग्री)</span>
                <div className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-lg px-1.5 py-1" data-testid="body-toolbar">
                  <button type="button" onClick={insertLink} data-testid="body-insert-link"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white text-xs text-gray-700 hover:text-[#D92525]" title="लिंक जोड़ें">
                    <Link2 size={13} /> लिंक
                  </button>
                  <label className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white text-xs text-gray-700 hover:text-[#D92525] cursor-pointer" title="फोटो जोड़ें" data-testid="body-insert-image-label">
                    {insertingImg ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />}
                    फोटो
                    <input
                      ref={inlineImgRef}
                      type="file"
                      accept="image/*"
                      onChange={insertInlineImage}
                      disabled={insertingImg}
                      data-testid="body-insert-image"
                      className="hidden"
                    />
                  </label>
                  <span className="text-[10px] text-gray-400 ml-1 hidden sm:inline">Cursor जहाँ हो, वहाँ जुड़ेगा</span>
                </div>
              </div>
              <textarea
                ref={bodyRef}
                data-testid="field-body"
                value={form.body}
                onChange={(e) => up("body", e.target.value)}
                rows={12}
                className="mt-1 w-full border border-gray-200 rounded-lg focus:border-[#D92525] outline-none p-3 text-[16px] leading-relaxed resize-y font-serif-dev"
                placeholder="पूरी खबर यहाँ लिखें... एक अलग line पर URL डालें (YouTube/Twitter/Instagram) — auto-embed होगा"
              />
              <p className="mt-1.5 text-[11px] text-gray-400">
                Tip: लिंक जोड़ने के लिए text select करके "लिंक" बटन दबाएँ। Photo सीधे upload होकर article में जुड़ जाएगी।
              </p>
            </label>
          </div>
        </div>

        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-4">
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">श्रेणी</span>
              <select data-testid="field-category" value={form.category} onChange={(e) => up("category", e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg focus:border-[#D92525] outline-none py-2.5 px-3 text-[15px] bg-white">
                {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">स्थान/किकर</span>
              <input data-testid="field-kicker" value={form.kicker} onChange={(e) => up("kicker", e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg focus:border-[#D92525] outline-none py-2 px-3 text-[15px]" placeholder="जैसे: इटारसी" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">फोटो</span>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <label data-testid="upload-label" className="inline-flex items-center gap-2 cursor-pointer bg-[#0A0F1E] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#D92525] transition-colors">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImageIcon size={14} />}
                  {uploading ? "अपलोड हो रहा…" : "इमेज अपलोड करें"}
                  <input data-testid="field-upload" type="file" accept="image/*" onChange={onUpload} className="hidden" />
                </label>
                {editing && (
                  <button
                    type="button"
                    onClick={genAiImage}
                    disabled={aiImgBusy}
                    data-testid="ai-image-btn"
                    title="Category के हिसाब से free Unsplash cover image लगाओ"
                    className="inline-flex items-center gap-1.5 bg-[#0F0F0F] text-white text-xs font-semibold px-3 py-2 rounded-lg hover:bg-[#D92525] disabled:opacity-60"
                  >
                    {aiImgBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                    {aiImgBusy ? "ला रहा…" : "Free Stock Image लाओ"}
                  </button>
                )}
              </div>
              <input data-testid="field-thumbnail" value={form.thumbnail} onChange={(e) => up("thumbnail", e.target.value)}
                className="mt-2 w-full border border-gray-200 rounded-lg focus:border-[#D92525] outline-none py-2 px-3 text-[13px]" placeholder="या फोटो URL पेस्ट करें (खाली छोड़ें तो DP लोगो अपने-आप लगेगा)" />
              {form.thumbnail ? (
                <img src={form.thumbnail} alt="" className="mt-3 w-full aspect-video object-cover rounded-lg bg-gray-100" />
              ) : (
                <div className="mt-3 w-full aspect-video rounded-lg bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center text-gray-300"><ImageIcon size={28} /></div>
              )}
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-[0.18em] text-gray-500">URL स्लग (SEO)</span>
              <input data-testid="field-slug" value={form.slug} onChange={(e) => up("slug", e.target.value)}
                className="mt-1 w-full border border-gray-200 rounded-lg focus:border-[#D92525] outline-none py-2 px-3 text-[13px]" placeholder="खाली छोड़ें तो शीर्षक से अपने-आप बनेगा" />
            </label>

            {canPublish && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input data-testid="field-denva" type="checkbox" checked={form.publish_as_denva} onChange={(e) => up("publish_as_denva", e.target.checked)} className="mt-1 accent-[#D92525]" />
                <span className="text-sm text-gray-600">"देनवा पोस्ट" के नाम से प्रकाशित करें (फोटो में DP लोगो)</span>
              </label>
            )}
            {form.category === "exclusive" && (
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_exclusive} onChange={(e) => up("is_exclusive", e.target.checked)} className="mt-1 accent-[#D92525]" />
                <span className="text-sm text-gray-600">एक्सक्लूसिव स्टोरी के रूप में चिह्नित करें</span>
              </label>
            )}
          </div>

          {canPublish && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-4" data-testid="homepage-controls">
              <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 font-semibold">होम पेज पर स्थान</div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input data-testid="field-hero" type="checkbox" checked={form.is_hero} onChange={(e) => up("is_hero", e.target.checked)} className="mt-1 accent-[#D92525]" />
                <span className="text-sm text-gray-700">मुख्य <b>हीरो</b> खबर बनाएँ (सबसे ऊपर बड़ी लीड)। एक समय पर सिर्फ़ एक।</span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input data-testid="field-pinned" type="checkbox" checked={form.pinned} onChange={(e) => up("pinned", e.target.checked)} className="mt-1 accent-[#D92525]" />
                <span className="text-sm text-gray-700">इस श्रेणी सेक्शन में <b>पिन</b> करें (पुरानी खबर भी ऊपर आ जाएगी)।</span>
              </label>
              {form.pinned && (
                <label className="block">
                  <span className="text-[11px] uppercase tracking-wider text-gray-500">क्रम (1 = सबसे पहले)</span>
                  <input data-testid="field-pinrank" type="number" min="1" value={form.pin_rank} onChange={(e) => up("pin_rank", parseInt(e.target.value) || 1)}
                    className="mt-1 w-24 border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" />
                </label>
              )}
              <p className="text-xs text-gray-400">कुछ न चुनें तो सेक्शन में अपने-आप latest खबरें क्रम से दिखेंगी।</p>
            </div>
          )}

          <div className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm space-y-3">
            <button data-testid="save-draft" disabled={loading} onClick={() => save(null)}
              className="w-full flex items-center justify-center gap-2 border border-gray-300 rounded-lg py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} ड्राफ्ट सहेजें
            </button>
            {canPublish ? (
              <button data-testid="publish-btn" disabled={loading} onClick={() => save("published")}
                className="w-full flex items-center justify-center gap-2 bg-[#D92525] text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-[#0A0F1E] transition-colors disabled:opacity-60">
                <CheckCircle2 size={16} /> प्रकाशित करें
              </button>
            ) : (
              <button data-testid="submit-review-btn" disabled={loading} onClick={() => save("pending")}
                className="w-full flex items-center justify-center gap-2 bg-amber-500 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-60">
                <Send size={16} /> समीक्षा हेतु भेजें
              </button>
            )}
            {editing && <div className="text-center text-xs text-gray-400">वर्तमान स्थिति: {status}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
